<template>
  <div
    :class="consumerClasses"
    :data-cube="this.consumer.cubeName"
    :id="`c_${this.consumer.cubeName}`"
  >
    <a class="consumer__left" @click="showProducerInMap()">
      <img
        class="consumer__img"
        :src="$store.getters.cubeIconUrl(this.consumer, 'svg')"
      />
      <h4 class="consumer__title">{{ nickName }}</h4>
      <span
        v-if="unreadNotifications.length > 0"
        class="consumer__has-notification"
        :title="str('notifications.title')"
        @click.capture.stop="showNotifications = !showNotifications"
      ></span>
    </a>
    <div class="consumer__content">
      <div class="consumer__content-top">
        <div class="consumer__priority">
          <div class="consumer__priority-title">Priorität</div>
          <Choice
            name="priority"
            :default="priority"
            :items="priorities"
            @selection-changed="onPriorityChanged"
          />
        </div>
      </div>
      <div class="consumer__content-bottom">
        <output class="consumer__current-price">
          <Price :label="str('efriends.currentPrice')" :value="currentPrice" />
        </output>
        <output class="consumer__next-price" v-if="nextPrice !== false">
          <Price :label="nextPriceLabel" :value="parseFloat(nextPrice)" />
        </output>
        <a
          :class="toggleEditPriceClasses"
          @click="toggleEditForm"
          title="Bearbeiten"
        >
          <IconEdit />
        </a>
      </div>
      <div :class="editClasses">
        <ToggleButton
          text="Sonderpreis"
          icon-left="Tag"
          :is-checked="hasSpecialPrice"
          :extra-classes="['consumer__toggle-special-price']"
          @change="onSpecialPriceToggled"
        />
        <div class="consumer__selling-price">
          <input
            type="range"
            :min="minSellingPrice"
            :max="maxSellingPrice"
            :step="sellingPriceStepSize"
            v-model="priceInputValue"
            :disabled="!hasSpecialPrice"
            @input="onPriceChanged"
          />
          <div class="consumer__selling-price-legend">
            <span class="consumer__selling-price-legend-min">{{
              formatPowerPrice(minSellingPrice, false)
            }}</span>
            <span class="consumer__selling-price-legend-text">
              {{ formatPowerPrice(priceInputValue) }}
            </span>
            <span class="consumer__selling-price-legend-max">{{
              formatPowerPrice(maxSellingPrice, false)
            }}</span>
          </div>
        </div>
      </div>
      <div :class="submitClasses">
        <a class="consumer__cancel" @click="cancel()">abbrechen</a>

        <LinkButton
          type="small"
          icon-right="Arrow"
          :form="`consumers_${this.consumer.cubeName}`"
          :extra-classes="['button--primary consumer__save']"
          :text="str('global.save')"
          @click.native="saveChanges()"
        />
      </div>
    </div>
    <CubeNotifications
      v-if="showNotifications"
      :notifications="unreadNotifications"
      :cube-name="consumer.cubeName"
      :extra-classes="['consumer__notifications']"
    />
  </div>
</template>

<script>
  import LinkButton from '@/components/LinkButton'
  import ToggleButton from '@/components/ToggleButton'
  import Choice from '@/components/Choice'
  import Price from '@/components/Price'
  import IconEdit from '@/components/icons/Edit'
  import util from '@/js/Util'
  import CubeNotifications from '@/components/efriends/CubeNotifications'

  export default {
    name: 'consumer',
    props: {
      consumer: {
        type: Object,
        required: true,
      },
    },
    components: {
      ToggleButton,
      LinkButton,
      Choice,
      Price,
      IconEdit,
      CubeNotifications,
    },
    data() {
      return {
        hasSpecialPrice: false,
        currentPrice: false,
        nextPrice: false,
        dateNextPrice: false,
        originalNextPrice: false,
        originallyHadSpecialPrice: false,
        originalPriority: 'default',
        priority: false,
        isEditing: false,
        priceInputValue: false,
        showNotifications: false,
      }
    },
    // We need to use created here to preserve state correctly.
    created() {
      this.initializeValues()
    },
    computed: {
      consumerClasses() {
        let result = ['consumer']
        const cubeNameRegex = new RegExp(`c_${this.consumer.cubeName}`)
        if (
          this.$route.name === 'efriends' &&
          this.$route.hash.match(cubeNameRegex)
        ) {
          result.push('consumer--highlighted')
        }
        return result.join(' ')
      },
      minSellingPrice: function () {
        return this.$store.getters.minDefaultSellingPrice
      },
      maxSellingPrice: function () {
        return this.$store.getters.maxDefaultSellingPrice
      },
      sellingPriceStepSize: function () {
        return this.$store.getters.sellingPriceStepSize
      },
      priorities: function () {
        return this.$store.getters.connectionPriorities
      },
      nickName: function () {
        return this.consumer.params.nickName
      },
      nextPriceLabel: function () {
        if (this.dateNextPrice && this.nextPrice === this.originalNextPrice) {
          return `Ab ${util.formatPriceDate(this.dateNextPrice)}`
        } else {
          return 'Neuer Preis'
        }
      },
      editClasses() {
        let classes = ['consumer__edit']
        if (this.isEditing) {
          classes.push('consumer__edit--expanded')
        }
        return classes.join(' ')
      },
      submitClasses() {
        let classes = ['consumer__submit-buttons']
        if (this.isChanged()) {
          classes.push('consumer__submit-buttons--expanded')
        }
        return classes.join(' ')
      },
      toggleEditPriceClasses() {
        let classes = ['consumer__edit-price']
        if (this.isEditing) {
          classes.push('consumer__edit-price--active')
        }
        return classes.join(' ')
      },
      pricesFromStore() {
        return this.$store.getters.consumerPrices(this.consumer.cubeName)
      },
      defaultPricesFromStore() {
        return this.$store.getters.defaultSellingPrices
      },
      unreadNotifications() {
        return this.$store.getters
          .notificationsForCube(this.consumer.cubeName)
          .filter((notification) => {
            return notification.unread === 1
          })
      },
    },
    methods: {
      initializeValues: function () {
        const prices = this.$store.getters.consumerPrices(
          this.consumer.cubeName
        )

        if (!prices) {
          // Fall back to the default selling price if no special price is set for
          // this cube.
          const defaultPrices = this.$store.getters.defaultSellingPrices
          this.currentPrice = defaultPrices.currentPrice
          if (typeof defaultPrices.nextPrice !== 'undefined') {
            this.nextPrice = defaultPrices.nextPrice
            this.originalNextPrice = defaultPrices.nextPrice
            this.dateNextPrice = defaultPrices.dateNextPrice
          }
        } else {
          this.hasSpecialPrice = true
          this.originallyHadSpecialPrice = true

          if (typeof prices.currentPrice !== 'undefined') {
            this.currentPrice = prices.currentPrice
          } else {
            // Fallback to the default price.
            const defaultPrices = this.$store.getters.defaultSellingPrices
            this.currentPrice = defaultPrices.currentPrice
          }

          if (typeof prices.nextPrice !== 'undefined') {
            this.nextPrice = prices.nextPrice
            this.originalNextPrice = prices.nextPrice
            this.dateNextPrice = prices.dateNextPrice
          }
        }

        if (this.nextPrice !== false) {
          this.priceInputValue = this.nextPrice
        } else {
          this.priceInputValue = this.currentPrice
        }

        this.priority = this.$store.getters.connectionPriority(this.consumer)
        this.originalPriority = this.priority
      },
      saveChanges: async function () {
        let promises = []

        this.$store.commit('setFormState', {
          form: `consumers_${this.consumer.cubeName}`,
          state: 'submitting',
        })

        if (
          this.hasSpecialPrice &&
          this.nextPrice !== false &&
          this.nextPrice !== this.originalNextPrice
        ) {
          promises.push(
            this.$store.dispatch('saveSpecialSellingPriceForConsumer', {
              price: parseFloat(this.nextPrice),
              consumer: this.consumer.cubeName,
            })
          )
        }

        if (this.originallyHadSpecialPrice && !this.hasSpecialPrice) {
          // Remove the special price.
          promises.push(
            this.$store.dispatch(
              'removeSpecialPriceForConsumer',
              this.consumer.cubeName
            )
          )
        }

        if (this.priority !== this.originalPriority) {
          promises.push(
            this.$store.dispatch('setConnectionPriority', {
              cubeName: this.consumer.cubeName,
              priority: this.priority,
              forConsumers: true,
            })
          )
        }

        await Promise.all(promises)
        this.$store.commit('setFormState', {
          form: `consumers_${this.consumer.cubeName}`,
          state: 'finished',
        })

        this.originalNextPrice = this.nextPrice
        this.originallyHadSpecialPrice = this.hasSpecialPrice
        this.originalPriority = this.priority
        this.isEditing = false
      },
      isChanged: function () {
        const specialPriceChanged =
          this.hasSpecialPrice !== this.originallyHadSpecialPrice
        const priceChanged = this.nextPrice !== this.originalNextPrice
        const priorityChanged = this.priority !== this.originalPriority

        return specialPriceChanged || priceChanged || priorityChanged
      },
      onSpecialPriceToggled: function (checked) {
        if (!checked) {
          const defaultPrices = this.$store.getters.defaultSellingPrices
          this.currentPrice = defaultPrices.currentPrice
          this.nextPrice = false
          this.dateNextPrice = false
          if (typeof defaultPrices.nextPrice !== 'undefined') {
            this.nextPrice = defaultPrices.nextPrice
            this.dateNextPrice = defaultPrices.dateNextPrice
          }
        }
        this.hasSpecialPrice = checked
      },
      onPriorityChanged: function (newPriority) {
        this.priority = newPriority
      },
      onPriceChanged: function () {
        this.nextPrice = this.priceInputValue
      },
      toggleEditForm: function () {
        this.isEditing = !this.isEditing
      },
      cancel: function () {
        this.isEditing = false
        this.hasSpecialPrice = this.originallyHadSpecialPrice
        this.initializeValues()
      },
      showProducerInMap: function () {
        this.$router.push({
          name: 'map',
          params: {
            selected: this.consumer.cubeName,
          },
        })
      },
    },
    watch: {
      pricesFromStore: function () {
        this.initializeValues()
      },
      defaultPricesFromStore: function () {
        this.initializeValues()
      },
    },
  }
</script>

<style lang="scss" scoped>
  .consumer {
    padding: spacer(1) 0;
    position: relative;
    display: flex;
    flex-wrap: wrap;

    &--highlighted {
      background: $grey-lightest;
    }

    &__left {
      margin-right: spacer(1);
      text-align: center;
      transition: $transition-base;
      width: $cube-list-img-size-min;
      flex-shrink: 0;
      cursor: pointer;
      position: relative;

      @media (min-width: $bp-xs) {
        width: $cube-list-img-size-xs;
      }

      @media (min-width: $bp-sm) {
        flex-basis: $efriends-page-icon-width-sm;
      }
    }

    &__img {
      padding: 0;
      max-width: 100%;

      @media (min-width: $bp-ssm) {
        padding: 0 spacer(0.5);
      }

      @media (min-width: $bp-sm) {
        padding: 0 spacer(2);
      }
    }

    &__title {
      margin: 0;
      text-align: center;
      font-size: $font-size-sm;
      line-height: $line-height-min;
      transition: $transition-base;
      font-weight: 500;

      @media (min-width: $bp-ssm) {
        font-size: $font-size-base;
      }
    }

    &__has-notification {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 0.75em;
      height: 0.75em;
      top: spacer(0.1) * -1;
      left: spacer(0.1) * -1;
      position: absolute;
      background: $notification-color;
      padding: 0.25em;
      border-radius: 100%;
      color: $white;

      @media (min-width: $bp-sm) {
        top: spacer(0.5);
        left: spacer(1);
        width: 1em;
        height: 1em;
      }
    }

    &__label {
      font-size: $font-size-sm;
      color: $grey-dark;

      @media (min-width: $bp-xs) {
        font-size: $font-size-base;
      }
    }

    &__toggle-special-price {
      margin-top: spacer(0.5);
      margin-left: auto;

      @media (min-width: $bp-md) {
        float: right;
        margin-right: 0;
      }
    }

    &__priority {
      margin-right: spacer(0.5);

      &-title {
        font-size: $font-size-sm;
        color: $grey-dark;
        line-height: $line-height-sm;
      }
    }

    &__edit-price {
      justify-self: flex-end;
      cursor: pointer;
      align-self: flex-end;
      margin-bottom: spacer(0.1);
      transition: $transition-base;
      padding: 0 spacer(0.25);
      border-radius: $border-radius;
      margin-left: auto;

      @media (min-width: $bp-sm) {
        padding: spacer(0.25) spacer(0.5);
      }

      @media (min-width: $bp-md) {
        margin-left: 0;
        align-self: flex-start;
        margin-top: spacer(1);
      }

      &--active {
        background: $grey-dark;
        color: $white;
      }

      @include hover-focus-active {
        background: $grey-dark;
        color: $white;
      }

      .icon {
        width: 1em;
        vertical-align: middle;
        // Vertical visual alignment.
        margin-top: spacer(0.1) * -1;
      }
    }

    &__content {
      flex-grow: 1;

      @media (min-width: $bp-md) {
        display: flex;
        flex-wrap: wrap;
        // Only when setting a width that is smaller than the available space
        // this will fit onto the same line as left.
        width: 30%;
      }

      &-top {
        display: flex;
        flex-wrap: wrap;
      }

      &-bottom {
        margin-top: spacer(1);
        display: flex;
        justify-self: flex-end;
        margin-left: auto;
        flex-grow: 1;
        flex-basis: 40%;

        @media (min-width: $bp-xs) {
          justify-content: flex-end;
        }

        @media (min-width: $bp-md) {
          margin-top: 0;
        }
      }
    }

    &__selling-price {
      &-legend {
        display: flex;
        justify-content: space-between;
      }
    }

    &__edit {
      max-height: 0;
      overflow: hidden;
      transition: $transition-base;
      margin-bottom: spacer(0.5);

      @media (min-width: $bp-sm) {
        flex-basis: 100%;
      }

      &--expanded {
        max-height: 200px;
      }

      &-bottom {
        margin-top: spacer(0.5);
        display: flex;
        justify-content: space-between;
      }
    }

    &__submit-buttons {
      max-height: 0;
      overflow: hidden;
      transition: $transition-base;
      display: flex;
      justify-content: space-between;
      align-items: center;

      @media (min-width: $bp-md) {
        justify-content: flex-end;
        flex-basis: 100%;
      }

      &--expanded {
        max-height: 60px;
      }
    }

    &__cancel {
      cursor: pointer;
      transition: $transition-base;
    }

    &__save {
      margin-left: spacer(1);
    }
  }
</style>
